import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Mail, Phone, MapPin, Clock, Gift } from "lucide-react";
import { useCurrency } from "../contexts/CurrencyContext";
import { SEO } from "../components/SEO";
import {
  EMPTY_FORM,
  buildInitialFormData,
  submitContactForm
} from "./contact/contactFormHelpers";

const SOURCE_BANNERS = {
  "credit-claim": {
    icon: Gift,
    title: "Claiming your $500 Credit",
    desc: "Drop your details below — our team will set up your account and apply your credit after you spend $1,000 in your first 60 days."
  }
};

const ContactPageInner = ({ source }) => {
  const sourceBanner = source ? SOURCE_BANNERS[source] : null;
  const navigate = useNavigate();
  const { contactInfo, isUAE } = useCurrency();

  const [formData, setFormData] = useState(() => buildInitialFormData(source));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    const result = await submitContactForm({ formData, isUAE, source });
    setSubmitStatus(result);
    if (result.type === "success") {
      setFormData(EMPTY_FORM);
    }
    setIsSubmitting(false);
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      <SEO
        title="Contact Us"
        description="Get in touch with FunAsia Digital. Ready to drive real customers to your business? Talk to our team about SmartReach™, foot-traffic marketing, and loyalty solutions."
        path="/contact"
      />
      <section className="section-padding bg-gradient-to-br from-orange-light via-white to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="font-display text-4xl md:text-5xl font-bold text-gray-dark mb-4">Contact Us</h1>
            <p className="text-gray-medium text-lg max-w-2xl mx-auto">
              Ready to grow your business? Get in touch with our team.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-white p-8 rounded-2xl shadow-card">
              {sourceBanner && (
                <div
                  className="mb-6 p-4 rounded-xl bg-gradient-to-br from-orange-light to-white border border-orange-primary/20 flex items-start gap-3"
                  data-testid="contact-source-banner"
                >
                  <div className="w-10 h-10 rounded-lg gradient-orange flex items-center justify-center flex-shrink-0">
                    <sourceBanner.icon className="text-white" size={20} />
                  </div>
                  <div>
                    <p className="font-display font-bold text-gray-dark text-base">{sourceBanner.title}</p>
                    <p className="text-sm text-gray-medium mt-1">{sourceBanner.desc}</p>
                  </div>
                </div>
              )}
              <h2 className="font-display text-2xl font-bold text-gray-dark mb-6">Send us a message</h2>
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-dark mb-1">Name *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary focus:border-transparent outline-none transition" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-dark mb-1">Email *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary focus:border-transparent outline-none transition" data-testid="contact-email-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-dark mb-1">Phone</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary focus:border-transparent outline-none transition" data-testid="contact-phone-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-dark mb-1">Business Name</label>
                    <input type="text" name="business_name" value={formData.business_name} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary focus:border-transparent outline-none transition" data-testid="contact-business-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-dark mb-1">Message *</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} required rows={4} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary focus:border-transparent outline-none transition resize-none" data-testid="contact-message-input" />
                  </div>
                </div>

                {submitStatus && (
                  <div className={`mt-4 p-4 rounded-lg ${submitStatus.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`} data-testid="contact-status-message">
                    {submitStatus.message}
                  </div>
                )}
                <button type="submit" disabled={isSubmitting} className="w-full btn-primary mt-6 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="contact-submit-btn">
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>

            <div className="space-y-6">
              <div className="bg-white p-6 rounded-2xl shadow-card">
                <h3 className="font-display text-xl font-bold text-gray-dark mb-4">Get in Touch</h3>
                <ul className="space-y-4">
                  <li className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-orange-light flex items-center justify-center">
                      <Mail className="text-orange-primary" size={20} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-medium">Email</p>
                      <p className="text-gray-dark font-medium">{contactInfo.email}</p>
                    </div>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-orange-light flex items-center justify-center">
                      <Phone className="text-orange-primary" size={20} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-medium">Phone</p>
                      <p className="text-gray-dark font-medium">{contactInfo.phone}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-orange-light flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-orange-primary" size={20} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-medium">Location</p>
                      {isUAE ? (
                        <>
                          <p className="text-gray-dark font-medium">{contactInfo.address}</p>
                          <p className="text-gray-dark">{contactInfo.addressLine2}</p>
                        </>
                      ) : (
                        <p className="text-gray-dark font-medium">{contactInfo.location}</p>
                      )}
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-orange-primary to-orange-secondary p-6 rounded-2xl text-white">
                <h3 className="font-display text-xl font-bold mb-2">Ready to get started?</h3>
                <p className="text-white/90 mb-4">Browse our services and sign up today.</p>
                <button onClick={() => navigate("/store")} className="bg-white text-orange-primary px-6 py-3 rounded-full font-semibold hover:shadow-lg transition-all" data-testid="contact-shop-btn">
                  Shop Services <ArrowRight size={18} className="inline ml-2" />
                </button>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-card">
                <div className="flex items-center gap-4">
                  <Clock className="text-orange-primary" size={32} />
                  <div>
                    <h4 className="font-display font-bold text-gray-dark">Business Hours</h4>
                    <p className="text-gray-medium text-sm">Monday - Friday: 9AM - 6PM {isUAE ? "GST" : "CST"}</p>
                    <p className="text-orange-primary text-sm font-medium">AI Support: 24/7</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Wrapper exists so the inner component remounts (and rebuilds initial state)
// whenever the lead `source` from router state changes. This avoids a fragile
// useEffect-driven prefill while still pre-filling messages for credit claims.
const ContactPage = () => {
  const location = useLocation();
  const source = location.state?.source || null;
  return <ContactPageInner key={source || "default"} source={source} />;
};

export default ContactPage;
